import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, Mail, MapPin, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Bitte geben Sie Ihren Namen an.";
    if (!form.email.trim()) er.email = "Bitte geben Sie Ihre E-Mail an.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) er.email = "Bitte prüfen Sie Ihre E-Mail-Adresse.";
    if (!form.message.trim() || form.message.trim().length < 5)
      er.message = "Bitte schreiben Sie ein paar Worte zu Ihrem Anliegen.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success("Anfrage gesendet", {
        description: data?.message || "Vielen Dank — ich melde mich zeitnah bei Ihnen.",
      });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Senden fehlgeschlagen", {
        description: "Bitte versuchen Sie es erneut oder schreiben Sie direkt an kontakt@trauma-fellmann.de.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10 relative">
        <div className="max-w-2xl">
          <p className="eyebrow reveal">Kontakt</p>
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 reveal reveal-delay-1"
            style={{ color: "var(--ink)" }}
          >
            Ich freue mich auf{" "}
            <em className="font-serif italic" style={{ color: "var(--sky-deep)" }}>
              Ihre Nachricht
            </em>
            .
          </h2>
          <p
            className="mt-6 max-w-xl leading-relaxed reveal reveal-delay-2"
            style={{ color: "var(--ink-soft)" }}
          >
            Ob Fragen, Anliegen oder der Wunsch nach Begleitung: Eine kurze Nachricht genügt.
            Ich melde mich zeitnah bei Ihnen zurück — telefonisch oder per E-Mail, ganz wie es
            für Sie passt.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-10 lg:gap-12">
          {/* Form */}
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-7 soft-card p-7 lg:p-10 reveal reveal-delay-2"
            noValidate
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className="block text-xs tracking-[0.2em] uppercase mb-2"
                  style={{ color: "var(--ink-mute)" }}>
                  Name
                </label>
                <input
                  id="name"
                  data-testid="contact-input-name"
                  type="text"
                  className="field-input"
                  placeholder="Ihr Name"
                  value={form.name}
                  onChange={update("name")}
                  aria-invalid={!!errors.name}
                />
                {errors.name && (
                  <p data-testid="contact-error-name" className="text-xs mt-2" style={{ color: "#B23A3A" }}>
                    {errors.name}
                  </p>
                )}
              </div>
              <div>
                <label htmlFor="email" className="block text-xs tracking-[0.2em] uppercase mb-2"
                  style={{ color: "var(--ink-mute)" }}>
                  E-Mail
                </label>
                <input
                  id="email"
                  data-testid="contact-input-email"
                  type="email"
                  className="field-input"
                  placeholder="ihre@email.de"
                  value={form.email}
                  onChange={update("email")}
                  aria-invalid={!!errors.email}
                />
                {errors.email && (
                  <p data-testid="contact-error-email" className="text-xs mt-2" style={{ color: "#B23A3A" }}>
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="message" className="block text-xs tracking-[0.2em] uppercase mb-2"
                style={{ color: "var(--ink-mute)" }}>
                Ihre Nachricht
              </label>
              <textarea
                id="message"
                data-testid="contact-input-message"
                rows={6}
                className="field-input resize-none"
                placeholder="Ihre Nachricht …"
                value={form.message}
                onChange={update("message")}
                aria-invalid={!!errors.message}
              />
              {errors.message && (
                <p data-testid="contact-error-message" className="text-xs mt-2" style={{ color: "#B23A3A" }}>
                  {errors.message}
                </p>
              )}
            </div>

            <p className="text-xs mt-5 leading-relaxed" style={{ color: "var(--ink-mute)" }}>
              Ihre Anfrage wird vertraulich behandelt. Mit dem Absenden erklären Sie sich mit unserer{" "}
              <a href="/datenschutz" className="soft-link">Datenschutzerklärung</a> einverstanden.
            </p>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="btn-primary mt-7 w-full sm:w-auto"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Wird gesendet…
                </>
              ) : (
                <>
                  Unverbindliche Anfrage senden
                  <Send size={17} />
                </>
              )}
            </button>
          </form>

          {/* Info card */}
          <aside className="lg:col-span-5 space-y-5">
            <div className="soft-card p-7 lg:p-8 reveal reveal-delay-3">
              <h3 className="font-serif text-2xl" style={{ color: "var(--ink)" }}>
                Kontakt direkt
              </h3>
              <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
                Lieber direkt schreiben oder anrufen? Ich freue mich, von Ihnen zu hören.
              </p>

              <ul className="mt-6 space-y-4 text-[15px]" style={{ color: "var(--ink)" }}>
                <li className="flex items-start gap-4">
                  <span className="icon-tile !w-11 !h-11"><Mail size={18} /></span>
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase" style={{ color: "var(--ink-mute)" }}>E-Mail</p>
                    <a href="mailto:kontakt@trauma-fellmann.de" className="soft-link" data-testid="contact-info-email">
                      kontakt@trauma-fellmann.de
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="icon-tile !w-11 !h-11"><Phone size={18} /></span>
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase" style={{ color: "var(--ink-mute)" }}>Telefon</p>
                    <a href="tel:+4915174408748" className="soft-link" data-testid="contact-info-phone">
                      +49 151 74408748
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <span className="icon-tile !w-11 !h-11"><MapPin size={18} /></span>
                  <div>
                    <p className="text-xs tracking-[0.2em] uppercase" style={{ color: "var(--ink-mute)" }}>Praxis</p>
                    <span>Eckernförderstraße 22 b · 24361 Haby</span>
                    <p className="text-xs mt-1" style={{ color: "var(--ink-mute)" }}>
                      Therapiesitzungen auch online via Zoom möglich.
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <div
              className="rounded-3xl p-7 lg:p-8 reveal reveal-delay-4"
              style={{ background: "var(--sky)", color: "var(--ink)" }}
            >
              <p className="font-serif italic text-xl leading-relaxed">
                {`„Der Weg zu mehr Wohlbefinden beginnt mit einem achtsamen Blick auf sich selbst."`}
              </p>
            </div>
          </aside>
        </div>

        {/* Anfahrt — Google Maps */}
        <div className="mt-16 lg:mt-20 reveal" data-testid="anfahrt-section">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <p className="eyebrow">Anfahrt</p>
              <h3
                className="font-display text-3xl sm:text-4xl mt-3"
                style={{ color: "var(--ink)" }}
              >
                Praxis in Haby
              </h3>
              <p className="mt-2 text-[15px]" style={{ color: "var(--ink-soft)" }}>
                Eckernförderstraße 22 b · 24361 Haby · Schleswig-Holstein
              </p>
            </div>
            <a
              href="https://www.google.com/maps/dir/?api=1&destination=Eckernf%C3%B6rderstra%C3%9Fe+22b%2C+24361+Haby"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="map-directions-link"
              className="btn-secondary !py-2.5 !px-4 !text-sm self-start"
            >
              Route planen
              <MapPin size={15} />
            </a>
          </div>

          <div
            className="mt-7 rounded-3xl overflow-hidden border shadow-lg"
            style={{ borderColor: "var(--line)" }}
            data-testid="map-embed-wrapper"
          >
            <iframe
              title="Karte: Praxis Seelen-Raum, Eckernförderstraße 22b, 24361 Haby"
              src="https://www.google.com/maps?q=Eckernf%C3%B6rderstra%C3%9Fe%2022b%2C%2024361%20Haby&output=embed"
              width="100%"
              height="420"
              style={{ border: 0, display: "block" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="map-iframe"
            />
          </div>
          <p className="mt-3 text-xs" style={{ color: "var(--ink-mute)" }}>
            Hinweis: Beim Anzeigen der Karte werden Daten an Google übertragen. Details siehe{" "}
            <a href="/datenschutz" className="soft-link">Datenschutzerklärung</a>.
          </p>
        </div>
      </div>
    </section>
  );
}
